import { c as _c } from "react/compiler-runtime";
import { AvatarPicture } from '@/components/ui/avatar-picture';
import { cn } from '@/utils/tailwindcss-override';
import type React from 'react';

// only shades that exist in the project palette; a missing one renders no background at all
const AVATAR_COLORS = ['bg-emerald-600', 'bg-teal-600', 'bg-amber-600', 'bg-conveniat-green', 'bg-slate-600'];

/**
 * Two initials for a name: the first letters of the first and the last word, e.g. "Anna Muster"
 * gives "AM". The Ceviname after "v/o" is left out, so "Anna Muster v/o Fuchs" is still "AM". A
 * single word gives its first two letters, "Fuchs" gives "FU".
 */
export const initialsOf = (name: string): string => {
  const realName = name.replace(/(?:^|\s+)v\/o\s.*$/i, '').trim();
  const source = realName === '' ? name.replace(/^\s*v\/o\s+/i, '') : realName;
  const words = source.trim().split(/\s+/).filter(Boolean);
  const first = [...(words[0] ?? '')];
  if (first.length === 0) return '?';
  if (words.length === 1) return first.slice(0, 2).join('').toUpperCase();
  const last = [...(words.at(-1) ?? '')];
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
};

/**
 * The same colours as text, a shade darker: a sender's name above their bubble in a group chat
 * takes the colour of their avatar and still reaches 4.5:1 on white.
 */
const AVATAR_TEXT_COLORS = ['text-emerald-700', 'text-teal-700', 'text-amber-700', 'text-conveniat-green', 'text-slate-700'];
const avatarColorIndexOf = (seed: string): number => {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 1_000_003;
  return hash % AVATAR_COLORS.length;
};

/** A stable colour per person, so they keep theirs across chats, lists and sessions. */
export const avatarColorOf = (seed: string): string => AVATAR_COLORS[avatarColorIndexOf(seed)] ?? 'bg-slate-600';

/** The text colour that goes with {@link avatarColorOf}, for the person's name. */
export const avatarTextColorOf = (seed: string): string => AVATAR_TEXT_COLORS[avatarColorIndexOf(seed)] ?? 'text-slate-700';

/**
 * A person's avatar: their profile picture from Cevi.DB, or their initials on a colour of their
 * own. The size and the text size come from `className`, e.g. `h-10 w-10 text-sm`.
 */
export const PersonAvatar: React.FC<{
  /** the person's id; decides the colour */
  seed: string;
  name: string;
  /** see `profilePictureUrl`; the initials show until and unless it loads */
  pictureUrl?: string | undefined;
  className?: string;
}> = t0 => {
  const $ = _c(11);
  const {
    seed,
    name,
    pictureUrl,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== seed) {
    t1 = cn("font-heading relative flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none", avatarColorOf(seed), className);
    $[0] = className;
    $[1] = seed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== name) {
    t2 = initialsOf(name);
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== pictureUrl) {
    t3 = pictureUrl !== undefined && <AvatarPicture src={pictureUrl} />;
    $[5] = pictureUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div aria-hidden="true" className={t1}>{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};